import React from 'react'
import { Form, Formik, FormikActions } from 'formik'
import { ObjectSchema } from 'yup'
import { Button, colors, Icon, Input, Select, Spinner, Typography } from '@dw/helix'
import { css } from '@emotion/core'

export interface TraceFormValues {
  'trace-event': string
  serial: string
}

interface Props {
  searchTypes: Record<string, string>
  traceFormInitialValues: TraceFormValues
  traceFormValidationSchema: ObjectSchema<TraceFormValues>
  onSubmit: (values: TraceFormValues, actions: FormikActions<TraceFormValues>) => void
  isFetchingData: boolean
  onTraceEventChange: (traceEvent: string) => void
}

const panelStyles = css`
  background-color: #fff;
  width: 100%;
  margin-top: 20px;
  padding: 18px 12px;
  border-radius: 2px;
  box-shadow: 0 1px 7px 0 rgba(0, 0, 0, 0.19), 0 3px 3px 0 rgba(0, 0, 0, 0.13);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`

const formStyles = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
`

const formWrapperStyles = css`
  min-width: 260px;
  margin-right: 20px;
`

const icoLeftStyles = css`
  margin-bottom: 1px;
`

export const SerialTracingForm: React.FunctionComponent<Props> = ({
  searchTypes,
  traceFormInitialValues,
  traceFormValidationSchema,
  onSubmit,
  isFetchingData,
  onTraceEventChange,
}) => {
  const options = Object.entries(searchTypes).map(([searchType, displayName]) => ({
    value: searchType,
    label: displayName,
  }))
  return (
    <div css={formWrapperStyles}>
      <Typography type="h2" className="e2e-test-trace-heading">
        Search
      </Typography>
      <div css={panelStyles}>
        <Formik
          initialValues={traceFormInitialValues}
          validationSchema={traceFormValidationSchema}
          enableReinitialize={true}
          isInitialValid={({ initialValues }: any) => traceFormValidationSchema.isValidSync(initialValues)}
          onSubmit={onSubmit}
          render={({ isValid, isSubmitting, setFieldValue, values, errors, resetForm }) => (
            <Form css={formStyles}>
              <Select
                value={values['trace-event']}
                options={options}
                onChange={e => {
                  const traceEvent = e.target.value
                  onTraceEventChange(traceEvent)
                  resetForm({ 'trace-event': traceEvent, serial: '' })
                }}
                name="trace-event"
                title="Search type"
                id="trace-event"
                css={css`
                  margin-bottom: 20px;
                `}
              />
              <Input
                value={values.serial}
                placeholder={`Search for ${searchTypes[values['trace-event']]}`}
                onChange={e => {
                  setFieldValue('serial', e.target.value)
                }}
                type="search"
                name="serial"
                icoLeft={<Icon name="search" size="s" css={icoLeftStyles} />}
                status={errors.serial ? `error` : undefined}
                hint={errors.serial}
                hasHint={true}
              />
              <Button
                type="submit"
                id="search-btn"
                disabled={!isValid || isSubmitting || isFetchingData}
                css={css`
                  margin-top: 12px;
                `}
              >
                {!(isSubmitting || isFetchingData) ? (
                  'Search'
                ) : (
                  <Spinner
                    color={colors.white}
                    css={css`
                      margin: -2px 0;
                    `}
                  />
                )}
              </Button>
            </Form>
          )}
        />
      </div>
    </div>
  )
}
